<template>
  <div ref="containerRef" class="fluid-container-custom mx-auto relative">
    <filtersFilterPanel v-model:open="openFilters"></filtersFilterPanel>
    <div class="mt-8 grid grid-cols-5">
      <div class="col-span-4">
        <div class="flex justify-start">
          <h1 class="text-2xl">Democracy Support Spending Flows {{baseYearRange[0]}} - {{baseYearRange[1]}}</h1>
          <HelpTooltip>
            Click on the drop-down arrows to arrange the data by name or donation value.<br/>
            This overview <span class="font-bold"> does not include </span> bilateral funding to unspecified recipients or multilateral organisations. For a more in-depth oveview, cliick on each donor. 
          </HelpTooltip>
        </div>
        <p class="font-sans mt-2">
          An overview revealing how donors spend on core democracy support. Hover the mouse over the donor, recipient, or category labels to learn more about where the funding is diverted to.
        </p>
      </div>
      <div class="text-right">
        <button
          class="px-4 py-1 rounded-md font-medium inline-flex gap-x-1.5"
          :class="[
            ifStore.isAnyFilterActive === true
              ? 'bg-Secondary-400 text-white'
              : 'bg-white text-Primary-800 ring-1 ring-Primary-800',
          ]"
          @click="openFilters = true"
        >
          Filter
          <AdjustmentsVerticalIcon
            class="-mr-1 h-5 w-5 text-Primary-800"
            :class="[
              ifStore.isAnyFilterActive === true
                ? 'text-white'
                : ' text-Primary-800 ',
            ]"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
    <div class="mb-4">
      <FiltersInlineFilters> </FiltersInlineFilters>
    </div>
    <SankeyHeaders :headers="headers" />
    <div class="sankey-wrapper" style="height:900px">
    <SankeyChart
      v-if="allLinks.length > 1 && allNodes.length > 1"
      :chart-data="sankeyData"
      :height="900"
      :total-width="availableWidth"
      :auto-sorting="
        headers[0].sortBy === sortByTypes.Auto &&
        headers[1].sortBy === sortByTypes.Auto &&
        headers[2].sortBy === sortByTypes.Auto
      "
      @nodeSelected="onNodeSelected"
      :extraBilateralInfo="false"
    />
    </div>
    <interfaceEntitySneakPeakModal
      v-model:showModal="showSneakPeakModal"
      :entityName="selectedEntity.name"
      :entityId="selectedEntity.id"
      :categories="selectedEntity.categories"
      :recipients="selectedEntity.topCountries"
      :contentType="selectedEntity.contentType"
      :nodeType="selectedEntity.nodeType"
      :token="token"
    >
    </interfaceEntitySneakPeakModal>
    <InterfaceSourcesText class="mt-24"></InterfaceSourcesText>
  </div>
</template>
<script setup>
import { queries } from "@/assets/queries.js";
import { AdjustmentsVerticalIcon } from "@heroicons/vue/20/solid";
import * as d3 from "d3";
import { httpConfig, baseYearRange } from "@/assets/baseTypes.js";
import { sortByTypes, nodeTypes } from "@/assets/configStrings.js";
import recipients from "@/assets/recipients.json";
import HelpTooltip from "~/components/HelpTooltip.vue";
detectScreenSize();
const config = useRuntimeConfig();

const containerRef = ref(null);
const openFilters = ref(false);

const { width: availableWidth } = useElementSize(containerRef);

const store = useBaseDataStore();
const ifStore = useInterfaceStore();
const token = computed(() => {
  return store.token;
});


await useManageAuth()

const reloadOnError = useReloadTokenOnError();

/*** sankey */
const { data: dataRecipients, refresh: refreshRecipients } = await useAsyncData(
  'getAllRecipientsByRegion' + ifStore.sliderYears[0] + ifStore.sliderYears[1],
  () => {
    const configAllRecipients = httpConfig(
      JSON.stringify(queries.getAllRecipientsByRegion(ifStore.sliderYears)),
      token.value
    );
    return $fetch(config.public.apiURL, configAllRecipients).catch(error => reloadOnError.refreshReload())
  }
);

const resultsForSankey1 = computed(() => {
  // this function will filter out the categories not selected in the filters
  let documents = dataRecipients.value.documents;
  if (ifStore.isFilterActive === true) {
    const filteredCategories = dataRecipients.value.documents.filter((item) => {
      return ifStore.activeFilterKeys.includes(item._id[1]);
    });
    documents = filteredCategories;
  }

  // get Total using d3.rollup
  const total = d3.sum(documents, (d) => d.TOTAL);
  // add percentage to the array
  documents.forEach((item) => {
    item.percentage = (item.TOTAL / total) * 100;
  });
  // END: low-contributions
  return documents;
});

const { data: dataCategoriesDonor, refresh: refreshDonors } =
  await useAsyncData(
    'getAllDonorsByCategory' + ifStore.sliderYears[0] + ifStore.sliderYears[1],
    () => {
    const configDonors = httpConfig(
      JSON.stringify(queries.getAllDonorsByCategory(ifStore.sliderYears)),
      token.value
    );
    return $fetch(config.public.apiURL, configDonors).catch(error => reloadOnError.refreshReload())
  });

const resultsForSankey2 = computed(() => {
  // this function will filter out the categories not selected in the filters
  if (ifStore.isFilterActive === false)
    return dataCategoriesDonor.value.documents;
  else {
    const filteredCategories = dataCategoriesDonor.value.documents.filter(
      (item) => {
        return ifStore.activeFilterKeys.includes(item._id[1]);
      }
    );
    return filteredCategories;
  }
});

// min height should be the number of recipients * 20

// list of nodes

// this function get all The donors with the aggregated sum using d3.rollup
const allDonors = computed(() => {
  const allDonorsMap = d3.rollup(
    resultsForSankey2.value,
    (v) => d3.sum(v, (d) => d.TOTAL),
    (d) => d._id[0]
  );
  // iterate over the map and return an array of objects
  let don = Array.from(allDonorsMap, ([name, value]) => ({
    _id: name,
    name: name,
    TOTAL: value,
    type: nodeTypes.donor,
  }));
  don = don.sort(sortFunctions[headers.value[0].sortBy]);
  return don;
});

const allCategories = computed(() => {
  const allCategoriesMap = d3.rollup(
    resultsForSankey2.value,
    (v) => d3.sum(v, (d) => d.TOTAL),
    (d) => d._id[1]
  );
  // iterate over the map and return an array of objects
  return Array.from(allCategoriesMap, ([name, value]) => ({
    _id: name,
    name: name,
    TOTAL: value,
    type: nodeTypes.category,
  })).sort(sortFunctions[headers.value[1].sortBy]);
});

const allRecipients = computed(() => {
  const allRecipientsMap = d3.rollup(
    resultsForSankey1.value,
    (v) => d3.sum(v, (d) => d.TOTAL),
    (d) => d._id[0]
  );
  return Array.from(allRecipientsMap, ([name, value]) => ({
    _id: name,
    name: name,
    TOTAL: value,
    type: nodeTypes.recipient,
  })).sort(sortFunctions[headers.value[2].sortBy]);
});

const sortFunctions = {
  [sortByTypes.Auto]: (a, b) => true,
  [sortByTypes.Name]: (a, b) => d3.ascending(a._id, b._id),
  [sortByTypes.Value]: (a, b) => d3.ascending(a.TOTAL, b.TOTAL),
};

const allNodes = computed(() => {
  let nodes = [];
  nodes = nodes.concat(allDonors.value);
  nodes = nodes.concat(allRecipients.value);
  nodes = nodes.concat(allCategories.value);
  return nodes.map((node) => {
    return {
      id: node._id,
      name: node._id,
      value: node.TOTAL,
      type: node.type,
    };
  });
});

const allLinks = computed(() => {
  let links = [];
  resultsForSankey2.value.forEach((item) => {
    links.push({
      source: item._id[0],
      target: item._id[1],
      value: item.TOTAL,
      id: item._id[0] + "-" + item._id[1],
    });
  });
  resultsForSankey1.value.forEach((item) => {
    links.push({
      source: item._id[1],
      target: item._id[0],
      value: item.TOTAL,
      id: item._id[1] + "-" + item._id[0],
    });
  });
  return links;
});

const sankeyData = computed(() => {
  return {
    nodes: allNodes.value,
    links: allLinks.value,
  };
});

watch(
  () => ifStore.sliderYears,
  (newValue, oldValue) => {
    refreshRecipients();
    refreshDonors();
  }
);

const headers = ref([
  {
    text: "Donors",
    sortBy: sortByTypes.Auto,
  },

  {
    text: "Categories",
    sortBy: sortByTypes.Auto,
  },
  {
    text: "Recipients",
    sortBy: sortByTypes.Auto,
  },
]);

// part in control of the sneakPeak Modal
const showSneakPeakModal = ref(false);
const selectedEntity = ref({
  name: "",
  id: "",
  type: nodeTypes.Donor,
  topCountries: [],
  topCategories: [],
});

const recipientsByRegion = d3.group(recipients, (d) => d.region);
const onNodeSelected = (node) => {
  if (node.type === nodeTypes.donor) {
    showSneakPeakModal.value = true;
    selectedEntity.value.name = node.name;
    selectedEntity.value.id = store.allDonorNames.find(
      (item) => item._id === node.name
    ).iso;
    selectedEntity.value.contentType = "single";
    selectedEntity.value.nodeType = node.type;
    selectedEntity.value.categories = node.sourceLinks.map((link) => {
      return {
        id: link.target.name,
        value: link.value,
      };
    });
  }
  if (node.type === nodeTypes.recipient) {
    showSneakPeakModal.value = true;
    selectedEntity.value.contentType = "multiple";
    selectedEntity.value.nodeType = node.type;
    selectedEntity.value.name = node.name;
    selectedEntity.value.topCountries = recipientsByRegion.get(node.name);

    //selectedEntity.value.topCountries =
  }
};
</script>
